/**
 * Offline mode state, pack lifecycle, and the arm/clear API.
 *
 * Design decisions worth knowing before editing:
 *
 * ARMING IS MANUAL, ENTERING IS AUTOMATIC. The user prepares a pack
 * deliberately ("before the flight"), but the app switches into offline mode
 * on its own the moment the network drops and a live pack exists. Nobody
 * remembers to flip a switch at 38,000 feet, and a mode you must remember to
 * enable is a mode that is off when you need it.
 *
 * A PACK EXPIRES. 48 hours by default, then it is wiped. Trade data, credit
 * limits and contract terms go stale, and a snapshot that lingers for a month
 * on a laptop is both a stale-decision risk and a data-at-rest risk.
 *
 * THE PACK IS PER USER. Switching accounts must not expose the previous
 * user's snapshot, so the stored user id is checked on load and a mismatch
 * wipes rather than reuses.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import { useAuth } from '../context/AuthContext';
import * as db from './db';
import * as worker from './worker';
import { setOfflineState } from './adapter';
import { clientVariantRequests } from './clientVariants';

const TTL_HOURS = 48;
const TTL_MS = TTL_HOURS * 60 * 60 * 1000;
const PAGE_SIZE = 25;

/** Binaries are the bulk of a pack; cap so a flight prep is not a 2 GB download. */
const MAX_FILES = 400;

/** How many times to wait out a 429 before giving up on a collection. */
const MAX_THROTTLE_RETRIES = 4;

const OfflineContext = createContext(null);

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Fetch one page, backing off and retrying when the API throttles us.
 *
 * A pack build is a burst of 150-400 requests, which trips IMT's own
 * `user: 600/min` DRF rate limit — observed in testing as ~38 collections
 * failing with 429 while the panel still reported success. The endpoints now
 * have their own throttle scope server-side, but a client that fires bursts
 * at an API must still handle 429 rather than treating it as a dead
 * collection: rate limiting is a "wait", not a "no".
 */
async function fetchPageWithRetry(key, offset, attempt = 0) {
  try {
    const res = await axiosInstance.get(`/api/offline-pack/collection/${key}/`, {
      params: { offset, limit: PAGE_SIZE },
      skipOfflineCache: true,
    });
    return res.data;
  } catch (e) {
    const status = e && e.response && e.response.status;
    if (status === 429 && attempt < MAX_THROTTLE_RETRIES) {
      // DRF sends Retry-After in seconds; fall back to exponential backoff.
      const header = e.response.headers && e.response.headers['retry-after'];
      const waitSec = Number(header);
      const waitMs = Number.isFinite(waitSec) && waitSec > 0
        ? Math.min(waitSec * 1000, 30000)
        : Math.min(1000 * 2 ** attempt, 8000);
      await sleep(waitMs);
      return fetchPageWithRetry(key, offset, attempt + 1);
    }
    if (status === 429) {
      const throttled = new Error('rate limited');
      throttled.throttled = true;
      throw throttled;
    }
    throw e;
  }
}

const IDLE = 'idle';
const PREPARING = 'preparing';
const READY = 'ready';
const ERROR = 'error';

export function OfflineProvider({ children }) {
  const [browserSupported] = useState(() => worker.isSupported());
  const { user } = useAuth();

  /* Offline mode is granted per user by an admin (Employee.has_offline_access,
   * ticked in the Role Assignment modal). Nobody has it until it is ticked --
   * deliberately NOT implied by has_full_access, which defaults to True.
   *
   * Three states, not two, because the flag arrives asynchronously and can also
   * fail to arrive at all (a cold boot with no network is exactly when this
   * feature is being used):
   *   granted        -- the payload arrived and says yes
   *   revoked        -- the payload arrived and says no  -> wipe what is stored
   *   unknown        -- not fetched yet, or the fetch failed while offline
   * "unknown" must never wipe a pack, or losing signal would delete the very
   * snapshot the user opened the laptop to read. */
  const grantKnown = !!user && Object.prototype.hasOwnProperty.call(user, 'has_offline_access');
  const granted = !!(user && user.has_offline_access);
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine !== false
  );
  const [forcedOffline, setForcedOffline] = useState(false);
  const [pack, setPack] = useState(null);
  const [status, setStatus] = useState(IDLE);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const [limits, setLimits] = useState([]);
  /* Whether packs also download DocuWare originals (server-side switch,
   * off by default). Drives the prepare-time slow-download warning. */
  const [includesDocuments, setIncludesDocuments] = useState(false);
  const [lastBlocked, setLastBlocked] = useState(null);
  /* Panel visibility lives here so the navbar entry and the dashboard strip
   * drive one shared modal instead of each owning a copy. */
  const [panelOpen, setPanelOpen] = useState(false);

  /* What THIS screen asked for and the pack did not have.
   *
   * The adapter already refuses to answer a miss with an empty success — it
   * throws OFFLINE_MISS precisely so "you have no deals" can never be
   * manufactured. But an error thrown at the axios layer is only as honest as
   * the component that catches it, and in the 2026-08-11 walkthrough several
   * caught it and rendered a confident zero anyway: "No Deliveries Yet", "No
   * Trade Recaps Yet", "No Product Items Found", and an invoice whose entity
   * and payment terms simply read "N/A".
   *
   * Fixing each of ~275 components is not a fix — the next screen someone
   * writes has the same bug. So the disclosure lives HERE, above every screen,
   * where no component's catch block can swallow it. A miss may still be
   * rendered as an empty list somewhere; it can no longer be rendered as an
   * empty list *silently*. */
  const [misses, setMisses] = useState([]);
  const [missesDismissed, setMissesDismissed] = useState(false);

  const cancelRef = useRef(false);
  const location = useLocation();

  const packLive = !!pack && pack.expiresAt > Date.now();
  /* What the entry points (navbar item, dashboard strip) key on. A live pack
   * keeps the panel reachable even mid-revoke: the door is already open, and
   * hiding the controls would only remove the user's ability to see or clear
   * what is on their own device. The revoke effect below removes the pack
   * itself the moment the server says the grant is gone. */
  const supported = browserSupported && (granted || packLive);
  const offline = (!online || forcedOffline) && packLive;

  /* Is the pack materially incomplete?
   *
   * Computed HERE, not in the components, because the dashboard strip and the
   * panel must never disagree — the strip was cheerfully reporting "Ready to
   * work offline" for a pack that was missing 34 of 69 sections while the
   * panel (correctly) called it incomplete. One source of truth, one verdict.
   * A single flaky section is noise; a tenth of them missing is a broken pack. */
  const failedSections = pack
    ? pack.collections.filter((c) => c.errorCount > 0).length
    : 0;
  const packIncomplete =
    !!pack && failedSections >= Math.max(2, pack.collections.length * 0.1);

  /* ------------------------------------------------------------ loading -- */

  const wipe = useCallback(async () => {
    await db.clearAll().catch(() => {});
    await worker.clearPack().catch(() => {});
    await worker.unregister().catch(() => {});
    setPack(null);
    setStatus(IDLE);
    setProgress(null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const stored = await db.getPack();
      if (cancelled || !stored) return;

      const currentUserId = localStorage.getItem('user_id');
      if (stored.expiresAt <= Date.now()) {
        await wipe();
        return;
      }
      // A pack belonging to a different account must never be served.
      if (currentUserId && String(stored.userId) !== String(currentUserId)) {
        await wipe();
        return;
      }
      setPack(stored);
      setStatus(READY);
    })();
    return () => {
      cancelled = true;
    };
  }, [wipe]);

  /* ----------------------------------------------------------- revoked -- */

  /* An admin un-ticking "Offline access" has to reach the laptop, not just the
   * server. Gating the API alone would leave a full read-only copy of HMS
   * sitting in IndexedDB for the rest of its 48h TTL -- the revoke would look
   * done and change nothing about what the user can actually still read.
   *
   * Only a KNOWN negative wipes. An absent flag means we have not heard from
   * the server (first paint, or offline), and guessing "revoked" there would
   * delete a valid pack at the exact moment it is needed. */
  useEffect(() => {
    if (!grantKnown || granted) return;
    let cancelled = false;
    (async () => {
      const stored = await db.getPack();
      if (cancelled || !stored) return;
      await wipe();
      setPanelOpen(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [grantKnown, granted, wipe]);

  /* ------------------------------------------------- connectivity events -- */

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  /* A miss belongs to the screen that caused it. Navigating away clears the
   * list, so the bar can never describe the previous page — the same mistake
   * the blocked-write toast made before it was given an auto-dismiss. */
  useEffect(() => {
    setMisses([]);
    setMissesDismissed(false);
  }, [location.pathname]);

  const recordMiss = useCallback((info) => {
    const url = (info && info.url) || '';
    if (!url) return;
    setMisses((prev) => (prev.some((m) => m.url === url) ? prev : [...prev, { url, at: Date.now() }]));
  }, []);

  /* Keep the axios adapter in sync. It lives outside React because
   * interceptors run outside the component tree. */
  useEffect(() => {
    setOfflineState({
      offline,
      packLive,
      generatedAt: pack ? pack.generatedAt : null,
      onBlockedWrite: (info) => setLastBlocked({ ...info, at: Date.now() }),
      onMiss: recordMiss,
    });
  }, [offline, packLive, pack, recordMiss]);

  /* Load the "what won't work offline" list when the panel first opens.
   *
   * It used to be fetched only during prepare(), which meant it appeared
   * *after* the user had committed — exactly backwards. It is the information
   * you want while deciding. Cheap, static, and failure here is silent
   * because the disclosure is supplementary. */
  useEffect(() => {
    if (!panelOpen || limits.length || !online) return;
    let cancelled = false;
    axiosInstance
      .get('/api/offline-pack/limits/', { skipOfflineCache: true })
      .then((res) => {
        if (cancelled) return;
        setLimits(res.data.unavailable || []);
        setIncludesDocuments(!!res.data.includes_documents);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [panelOpen, limits.length, online]);

  /* Expire the pack the moment its TTL elapses while the app is open. */
  useEffect(() => {
    if (!pack) return undefined;
    const remaining = pack.expiresAt - Date.now();
    if (remaining <= 0) {
      wipe();
      return undefined;
    }
    const timer = setTimeout(() => wipe(), remaining);
    return () => clearTimeout(timer);
  }, [pack, wipe]);

  /* ---------------------------------------------------------- preparing -- */

  const cancel = useCallback(() => {
    cancelRef.current = true;
  }, []);

  const prepare = useCallback(async () => {
    if (!browserSupported) {
      setError('This browser cannot store an offline pack.');
      setStatus(ERROR);
      return;
    }
    if (!granted) {
      setError('Offline access is not enabled for your account. Ask an administrator to switch it on.');
      setStatus(ERROR);
      return;
    }

    cancelRef.current = false;
    setStatus(PREPARING);
    setError(null);
    setProgress({ phase: 'Starting', done: 0, total: 1, label: '', files: 0 });

    try {
      await worker.register();

      setProgress((p) => ({ ...p, phase: 'Caching the app itself', label: '' }));
      await worker.precacheShell();

      // Fetch limits + plan. `skipOfflineCache` keeps the builder's own calls
      // from being answered out of a stale pack.
      const limitsRes = await axiosInstance.get('/api/offline-pack/limits/', {
        skipOfflineCache: true,
      });
      setLimits(limitsRes.data.unavailable || []);
      setIncludesDocuments(!!limitsRes.data.includes_documents);

      // The plan probe is the slowest single step and the most likely to fail
      // (it touches every collection). Show indeterminate progress rather than
      // a bar frozen at "0 of 1 — 0%", which reads as hung.
      setProgress({
        phase: 'Working out what you can take',
        done: 0, total: 0, label: 'Checking your access…', files: 0,
      });
      const planRes = await axiosInstance.get('/api/offline-pack/plan/', {
        skipOfflineCache: true,
      });
      const collections = (planRes.data.collections || []).filter((c) => c.accessible);

      /* Only NOW is the old pack discarded.
       *
       * This used to be the first line of prepare(), which meant a failure
       * during the plan — the slow, network-dependent, most-likely-to-fail
       * step — left the user with no pack at all. Observed twice in testing:
       * a backend restart mid-probe wiped a working snapshot and reported
       * only "Could not finish preparing your offline pack." Refreshing a
       * pack must never be able to leave you worse off than not trying. */
      await db.clearAll();

      const stats = [];
      const allFiles = [];
      const allImos = new Set();
      let storedCount = 0;
      /* Measured from the payloads we actually store. Previously this came
       * from navigator.storage.estimate(), which reports usage for the WHOLE
       * origin — every cache, every other feature's IndexedDB — and was
       * presented to the user as the size of their pack. */
      let dataBytes = 0;

      for (let i = 0; i < collections.length; i += 1) {
        if (cancelRef.current) throw new Error('cancelled');
        const collection = collections[i];

        setProgress({
          phase: 'Downloading your data',
          done: i,
          total: collections.length,
          label: collection.label,
          files: allFiles.length,
        });

        let offset = 0;
        let guard = 0;
        let recordCount = 0;
        let truncated = false;
        let listTruncated = false;
        const errors = [];

        // Walk pages until the server says there are no more. `guard` is a
        // belt-and-braces stop so a backend bug cannot spin forever.
        for (;;) {
          if (cancelRef.current) throw new Error('cancelled');
          guard += 1;
          if (guard > 200) break;

          let page;
          try {
            page = await fetchPageWithRetry(collection.key, offset);
          } catch (e) {
            // Keep the real reason. A section that silently "couldn't be
            // saved" with no cause is undiagnosable in the field — this is the
            // only signal anyone gets about why a pack came back short.
            const status = e && e.response && e.response.status;
            const reason = e && e.throttled
              ? 'rate limited'
              : status
                ? `HTTP ${status}`
                : `${(e && e.message) || 'request failed'}`;
            // eslint-disable-next-line no-console
            console.error(`[offline pack] ${collection.key} failed:`, reason, e);
            errors.push({ url: collection.key, error: reason });
            break;
          }

          if (page.entries && Object.keys(page.entries).length) {
            storedCount += await db.putResponses(page.entries);
            try {
              dataBytes += JSON.stringify(page.entries).length;
            } catch (e) {
              /* size is informational only; never fail a build over it */
            }
          }
          if (page.files && page.files.length) allFiles.push(...page.files);
          if (page.entries) db.harvestImos(page.entries, allImos);
          if (page.errors && page.errors.length) errors.push(...page.errors);

          recordCount = page.record_count || recordCount;
          truncated = truncated || !!page.truncated;
          listTruncated = listTruncated || !!page.list_truncated;

          if (!page.has_more) break;
          offset = page.next_offset != null ? page.next_offset : offset + PAGE_SIZE;
        }

        stats.push({
          key: collection.key,
          label: collection.label,
          group: collection.group,
          records: recordCount,
          truncated,
          listTruncated,
          errorCount: errors.length,
          failed: errors.length > 0,
          throttled: errors.some((e) => e.error === 'rate limited'),
        });
      }

      /* URLs only the browser can spell.
       *
       * The Trade Ops year filter is built from the LOCAL clock, so the server
       * cannot write it down (see clientVariants.js). These are fetched here,
       * through the same axios instance and the same helper the screen uses,
       * and stored under the key that screen will look up. Failures are
       * recorded as a normal collection so the panel says so out loud — a
       * silently missing dashboard filter is indistinguishable from a
       * dashboard with no data, which is the whole defect being fixed. */
      {
        const requests = clientVariantRequests();
        const variantErrors = [];
        let variantCount = 0;

        for (let i = 0; i < requests.length; i += 1) {
          if (cancelRef.current) throw new Error('cancelled');
          const { url, params, label } = requests[i];

          setProgress({
            phase: 'Saving your dashboard filters',
            done: i,
            total: requests.length,
            label,
            files: 0,
          });

          try {
            const res = await axiosInstance.get(url, { params, skipOfflineCache: true });
            const key = db.normalizeUrl(url, params);
            if (key) {
              storedCount += await db.putResponses({ [key.full]: res.data });
              variantCount += 1;
              try {
                dataBytes += JSON.stringify(res.data).length;
              } catch (e) {
                /* size is informational only */
              }
            }
          } catch (e) {
            const status = e && e.response && e.response.status;
            // A 403 is the access gate doing its job, not a failure: this user
            // simply has no Trade Ops. Anything else is worth reporting.
            if (status === 401 || status === 403) continue;
            variantErrors.push({ url: label, error: status ? `HTTP ${status}` : 'request failed' });
          }
        }

        if (variantCount || variantErrors.length) {
          stats.push({
            key: 'dashboard_filters',
            label: 'Dashboard filters',
            group: 'Trade Ops',
            records: variantCount,
            truncated: false,
            listTruncated: false,
            errorCount: variantErrors.length,
            failed: variantErrors.length > 0,
            throttled: false,
          });
        }
      }

      /* Last-known vessel positions.
       *
       * One batch call, after the data is in, using IMOs harvested from the
       * pack itself. Positions are a bonus: a failure here must never fail a
       * build, so it is fully swallowed. See VesselPositionsView for why
       * positions are cached but compliance verdicts deliberately are not. */
      let vesselPositions = null;
      try {
        const imos = [...allImos];
        if (imos.length) {
          setProgress({
            phase: 'Saving vessel positions',
            done: collections.length,
            total: collections.length,
            label: `${imos.length} vessel${imos.length === 1 ? '' : 's'}`,
            files: 0,
          });
          const res = await axiosInstance.post(
            '/api/offline-pack/vessel-positions/',
            { imo_list: imos },
            { skipOfflineCache: true }
          );
          vesselPositions = res.data;
          await db.putResponses({ [db.VESSEL_POSITIONS_KEY]: res.data });
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('[offline pack] vessel positions unavailable:', e && e.message);
      }

      /* Files last: they are the slow part and the most droppable. A pack with
       * data but no PDFs is still useful; the reverse is not. */
      const uniqueFiles = Array.from(new Set(allFiles));
      const filesToCache = uniqueFiles.slice(0, MAX_FILES);
      const filesSkipped = uniqueFiles.length - filesToCache.length;

      if (filesToCache.length) {
        setProgress({
          phase: 'Downloading documents',
          done: collections.length,
          total: collections.length,
          label: `${filesToCache.length} files`,
          files: filesToCache.length,
        });
        await worker.cacheFiles(filesToCache);
        await db.putFiles(filesToCache);
      }

      const now = Date.now();
      const record = {
        userId: localStorage.getItem('user_id'),
        generatedAt: now,
        expiresAt: now + TTL_MS,
        ttlHours: TTL_HOURS,
        responseCount: storedCount,
        fileCount: filesToCache.length,
        filesSkipped,
        collections: stats,
        sizeBytes: dataBytes,
        vesselCount: vesselPositions ? (vesselPositions.data || []).length : 0,
        vesselsCapturedAt: vesselPositions ? vesselPositions.captured_at : null,
      };

      await db.setPack(record);
      await worker.armPack({ generatedAt: now, expiresAt: record.expiresAt });

      setPack(record);
      setStatus(READY);
      setProgress(null);
    } catch (e) {
      if (e && e.message === 'cancelled') {
        await wipe();
        setStatus(IDLE);
        setError(null);
      } else {
        setError(
          (e && e.response && e.response.data && e.response.data.detail) ||
            'Could not finish preparing your offline pack.'
        );
        setStatus(ERROR);
      }
      setProgress(null);
    }
  }, [browserSupported, granted, wipe]);

  /* ------------------------------------------------------------- exports -- */

  const value = useMemo(
    () => ({
      supported,
      online,
      offline,
      packLive,
      pack,
      status,
      progress,
      error,
      limits,
      includesDocuments,
      lastBlocked,
      clearBlocked: () => setLastBlocked(null),
      misses,
      missesDismissed,
      dismissMisses: () => setMissesDismissed(true),
      forcedOffline,
      setForcedOffline,
      prepare,
      cancel,
      clear: wipe,
      isPreparing: status === PREPARING,
      staleness: pack ? Date.now() - pack.generatedAt : null,
      panelOpen,
      openPanel: () => setPanelOpen(true),
      closePanel: () => setPanelOpen(false),
      packIncomplete,
      failedSections,
    }),
    [
      supported, online, offline, packLive, pack, status, progress, error,
      limits, includesDocuments, lastBlocked, forcedOffline, prepare, cancel,
      wipe, panelOpen,
      packIncomplete, failedSections, misses, missesDismissed,
    ]
  );

  return <OfflineContext.Provider value={value}>{children}</OfflineContext.Provider>;
}

export function useOffline() {
  const ctx = useContext(OfflineContext);
  if (!ctx) {
    // Callers outside the provider (tests, isolated stories) get a safe inert
    // shape rather than a crash.
    return {
      supported: false, online: true, offline: false, packLive: false,
      pack: null, status: IDLE, progress: null, error: null, limits: [],
      includesDocuments: false,
      misses: [], missesDismissed: false, dismissMisses: () => {},
      lastBlocked: null, clearBlocked: () => {}, forcedOffline: false,
      setForcedOffline: () => {}, prepare: async () => {}, cancel: () => {},
      clear: async () => {}, isPreparing: false, staleness: null,
      panelOpen: false, openPanel: () => {}, closePanel: () => {},
      packIncomplete: false, failedSections: 0,
    };
  }
  return ctx;
}

export default OfflineContext;
